import { useState, useEffect, useRef, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { userSettingsQueryOptions, useUpdateUserSettingsMutation } from '@/lib/query-options';

export const HIDDEN_TYPES = ['onboarding', 'standard', 'halo', 'focused'];
// 'retargeting' is Meta-only (#6182): Meta campaign types map onto this list as TESTING→prospecting,
// SCALING→hipo, RETARGETING→retargeting (see the sidebar's Meta filter).
const DEFAULT_TYPES = new Set(['brand', 'prospecting', 'competitor', 'hipo', 'icp', 'location', 'external', 'retargeting', ...HIDDEN_TYPES]);
const DEFAULT_STATUSES = new Set(['live', 'draft', 'paused', 'ended', 'archived']);
const DEFAULT_PLATFORMS = new Set(['google', 'meta']);

export function useCampaignSidebarFilters() {
  const { data: settings } = useQuery(userSettingsQueryOptions());
  const mutation = useUpdateUserSettingsMutation();
  const initialized = useRef(false);

  const [filterTypes, setFilterTypes] = useState<Set<string>>(DEFAULT_TYPES);
  const [filterStatuses, setFilterStatuses] = useState<Set<string>>(DEFAULT_STATUSES);
  const [filterPlatforms, setFilterPlatforms] = useState<Set<string>>(DEFAULT_PLATFORMS);

  // Initialize from persisted settings on first load
  useEffect(() => {
    if (settings && !initialized.current) {
      const saved = (settings.filters?.campaign_sidebar ?? {}) as {
        types?: string[];
        statuses?: string[];
        platforms?: string[];
      };
      if (saved.types) setFilterTypes(new Set([...saved.types, ...HIDDEN_TYPES]));
      if (saved.statuses) setFilterStatuses(new Set(saved.statuses));
      if (saved.platforms) setFilterPlatforms(new Set(saved.platforms));
      initialized.current = true;
    }
  }, [settings]);

  const updateFilters = useCallback((types: Set<string>, statuses: Set<string>, platforms: Set<string>) => {
    setFilterTypes(types);
    setFilterStatuses(statuses);
    setFilterPlatforms(platforms);

    // Persist only visible types (exclude hidden)
    mutation.mutate({
      filters: {
        campaign_sidebar: {
          types: [...types].filter(t => !HIDDEN_TYPES.includes(t)),
          statuses: [...statuses],
          platforms: [...platforms],
        },
      },
    });
  }, [mutation]);

  return { filterTypes, filterStatuses, filterPlatforms, updateFilters };
}
